<template>
  <div class="fixed inset-0 overflow-hidden z-max-1">
    <div v-if="store.resume.streaming" class="fixed top-4 right-4 flex items-center gap-4 rounded-md bg-green-600 px-4 py-3 text-white z-max">
      <AnimationSpinningLoader diameter="1rem"/>
      <p class="font-medium">
        <span>Auto-Complete {{ store.resume?.thinking ? 'Thinking' : 'Running' }} </span>
        <span class="animate-pulse" style="animation-delay: 0ms;">.</span>
        <span class="animate-pulse" style="animation-delay: 50ms;">.</span>
        <span class="animate-pulse" style="animation-delay: 1000ms;">.</span>
      </p>
    </div>

    <div :class="profilePanelClasses" ref="profilePanel"
         class="fixed lx:top-0 top-16 right-0 lx:right-auto bottom-16 left-0 lx:w-1/2 w-full overflow-y-auto transition-all">
      <CandidateProfile :candidate="store.candidate" @update:candidate="handleUpdateCandidate" continuous
                        editable class="mx-auto max-w-screen-lg"/>
    </div>
    <div :class="resumePanelClasses"
         class="fixed top-0 right-0 bottom-16 left-0 lx:left-auto lx:w-1/2 w-full overflow-hidden bg-black lx:pt-0 pt-16 transition-all">
      <ParseResumePreview class="w-full h-full"/>
    </div>
    <div
      class="fixed top-0 right-0 left-0 flex lx:hidden items-center justify-center gap-x-2 border-b-2 border-slate-200 bg-white p-2 shadow-md z-max-1">
      <FormButton @click="setSelectedPanel(PANELS.PROFILE)" :color="profilePanelColor" bold flex>
        <UserCircleIcon class="h-4 w-4"/>
        Profile
      </FormButton>
      <FormButton @click="setSelectedPanel(PANELS.RESUME)" :color="resumePanelColor" bold flex>
        <DocumentTextIcon class="h-4 w-4"/>
        Resume
      </FormButton>
    </div>
    <div
      class="fixed right-0 bottom-0 left-0 flex items-center justify-center gap-x-2 border-t-2 border-slate-200 bg-white p-2 shadow-md z-max-1">
      <template v-if="shouldAutoscroll">
        <FormButton @click="setShouldAutoscroll(false)" color="secondary" bold flex>
          <XMarkIcon class="h-4 w-4"/>
          Stop Scrolling with Auto-Complete
        </FormButton>
      </template>
      <template v-else-if="store.resume.streaming">
        <FormButton @click="setShouldAutoscroll(true)" color="gradient" bold flex>
          <CheckIcon class="h-4 w-4"/>
          Resume Scroll with Auto-Complete
        </FormButton>
      </template>
      <template v-else>
        <FormButton @click="setShowResetModal(true)" color="secondary" bold flex>
          <XMarkIcon class="h-4 w-4"/>
          Restart
        </FormButton>
        <FormButton id="confirmParseResumeInfo" @click="setShowConfirmModal(true)" color="gradient" bold flex>
          <CheckIcon class="h-4 w-4"/>
          Confirm Info
        </FormButton>
      </template>
    </div>
    <ModalConfirm v-model:open="showResetModal" title="Reset & upload new resume?"
                  proceed-button-text="Reset & Restart"
                  description="You will lose all the data you have entered so far and have to upload your resume again. You can only upload three resumes per hour, so make sure you have the correct one."
                  @confirm="store.reset"/>
    <ModalConfirm v-model:open="showConfirmModal" title="Almost There – Optionally Add Compensation"
                  class="z-tour-2"
                  level="success" hide-cancel-button
                  proceed-button-text="I'm Finished!"
                  proceed-button-id="confirmParseResumeInfo"
                  :description="confirmDescription"
                  @confirm="store.complete">
      <FormSliderPayAndHourlyVsSalary
        v-model:prefersHourlyOverSalary="store.candidate.prefersHourlyOverSalary"
        v-model:desiredHourlyPay="store.candidate.desiredHourlyPay"
        v-model:compensationFlexibility="store.candidate.compensationFlexibility"
        include-privacy-notice
        compact
        class="mt-4"/>
    </ModalConfirm>
  </div>
</template>

<script setup>
import AnimationSpinningLoader from "./AnimationSpinningLoader.vue";
import FormSliderPayAndHourlyVsSalary from "./FormSliderPayAndHourlyVsSalary.vue";
import CandidateProfile from "./CandidateProfile.vue";
import FormButton from "./FormButton.vue";
import ModalConfirm from "./ModalConfirm.vue";
import ParseResumePreview from "./ParseResumePreview.vue";
import {useParseResumeStore} from "../stores/ParseResumeStore.js";
import {computed, onBeforeUnmount, onMounted, ref, watch} from "vue";
import {CheckIcon, DocumentTextIcon, UserCircleIcon, XMarkIcon} from "@heroicons/vue/20/solid/index.js";
import {debounce} from "lodash";

const store = useParseResumeStore();

const PANELS = {
  PROFILE: "profile",
  RESUME: "resume",
};

const selectedPanelSm = ref(PANELS.PROFILE);
const showResetModal = ref(false);
const showConfirmModal = ref(false);
const profilePanel = ref(null);
const shouldAutoscroll = ref(true);

/** ––––––––––––––––––––––––––––––––––––––––––––––––––––––
 *                        Setters
 * –––––––––––––––––––––––––––––––––––––––––––––––––––––– */

const setSelectedPanel = (value) => selectedPanelSm.value = value;

const setShowResetModal = (value) => showResetModal.value = value;
const setShowConfirmModal = (value) => showConfirmModal.value = value;
const setShouldAutoscroll = (value) => shouldAutoscroll.value = value;

/** ––––––––––––––––––––––––––––––––––––––––––––––––––––––
 *                   Computed Values
 * –––––––––––––––––––––––––––––––––––––––––––––––––––––– */

const profilePanelClasses = computed(() => {
  if (selectedPanelSm.value !== PANELS.PROFILE) return "-translate-x-full lx:translate-x-0";
  return "";
});

const resumePanelClasses = computed(() => {
  if (selectedPanelSm.value !== PANELS.RESUME) return "translate-x-full lx:translate-x-0";
  return "";
});

const profilePanelColor = computed(() => selectedPanelSm.value === PANELS.PROFILE ? "primary" : "secondary");
const resumePanelColor = computed(() => selectedPanelSm.value === PANELS.RESUME ? "primary" : "secondary");

/** ––––––––––––––––––––––––––––––––––––––––––––––––––––––
 *                        Functions
 * –––––––––––––––––––––––––––––––––––––––––––––––––––––– */

const debounceSetTrustScore = debounce(store.setTrustScore, 3000);

const handleUpdateCandidate = (candidate) => {
  Object.assign(store.candidate, candidate);
  debounceSetTrustScore();
};

const handleCompleteStreaming = () => toast.info("Done! Review your profile and confirm your info.");

const imFinished = "I'm Finished!";
const confirmDescription = `Optionally add your desired compensation, then hit "${imFinished}" to complete this step.`;

/** ––––––––––––––––––––––––––––––––––––––––––––––––––––––
 *                        Watchers
 * –––––––––––––––––––––––––––––––––––––––––––––––––––––– */

const hasScrolledToLanguages = ref(false);

watch(() => store.candidate.languages, (newVal, oldVal) => {
  if (newVal?.length === oldVal?.length || !shouldAutoscroll.value || hasScrolledToLanguages.value) return;
  const basicInfo = document.getElementById("candidateBasicInfo");
  if (basicInfo) basicInfo.scrollIntoView({behavior: "smooth"});
  hasScrolledToLanguages.value = true;
});

const hasScrolledToCareerPassport = ref(false);

watch(() => store.candidate.careerPassport, (newVal, oldVal) => {
  if (newVal?.length === oldVal?.length || !shouldAutoscroll.value) return;
  const careerPassport = document.getElementById("candidateCareerPassport");
  if (careerPassport) careerPassport.scrollIntoView({behavior: "smooth"});
  hasScrolledToCareerPassport.value = true;
});

const hasScrolledToEducation = ref(false);

watch(() => store.candidate.education, (newVal, oldVal) => {
  if (newVal?.length === oldVal?.length || !shouldAutoscroll.value || hasScrolledToEducation.value) return;
  const education = document.getElementById("candidateEducation");
  if (education) education.scrollIntoView({behavior: "smooth"});
  hasScrolledToEducation.value = true;
});

const hasScrolledToSkills = ref(false);

watch(() => [store.candidate.skills, store.candidate.accomplishments], (newVal, oldVal) => {
  if (newVal?.length === oldVal?.length || !shouldAutoscroll.value || hasScrolledToSkills.value) return;
  const skills = document.getElementById("candidateSkillsAndAccomplishments");
  if (skills) skills.scrollIntoView({behavior: "smooth"});
  hasScrolledToSkills.value = true;
});

const hasScrolledToSocials = ref(false);

watch(() => [store.candidate.linkedIn, store.candidate.instagram, store.candidate.facebook, store.candidate.website], (newVal, oldVal) => {
  if (newVal === oldVal || !shouldAutoscroll.value || hasScrolledToSocials.value) return;
  const socials = document.getElementById("candidateSocials");
  if (socials) socials.scrollIntoView({behavior: "smooth"});
  hasScrolledToSocials.value = true;
});

watch(() => store.resume.streaming, (newVal, oldVal) => {
  if (newVal === oldVal) return;

  if (!newVal && oldVal) handleCompleteStreaming();

  if (!shouldAutoscroll.value) return;
  shouldAutoscroll.value = store.resume.streaming;
}, {immediate: true});

/** ––––––––––––––––––––––––––––––––––––––––––––––––––––––
 *                        Mounted
 * –––––––––––––––––––––––––––––––––––––––––––––––––––––– */

onMounted(() => document.body.classList.add("hide-scrollbar"));
onBeforeUnmount(() => document.body.classList.remove("hide-scrollbar"));
</script>
